import { Form, Formik, type FormikValues } from 'formik';
import { memo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type Board } from '@air/api/types';
import { FormikField } from '@air/formik-field';
import { Button } from '@air/primitive-button';
import { Input } from '@air/primitive-input';
import { Label } from '@air/primitive-label';
import { Modal, ModalCloseButton, ModalDescription, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { PrivateBoardSearch, type PrivateBoardSearchProps } from '~/components/BoardSearch/PrivateBoardSearch';
import { type BoardSearchLibrary, useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { type FormPageIsValidFunction } from '~/components/Modals/sharedTypes';
import { ToastLink } from '~/components/UI/ToastLink';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useGoToBoardPage } from '~/hooks/useGoToBoardPage';
import { useLibraryPermissionsCache } from '~/hooks/useLibraryPermissionsCache';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { centralizedBoardSelector } from '~/store/centralizedBoard/selectors';
import { useCreateNewBoard } from '~/swr-hooks/boards/useCreateNewBoard';
import { type InitialBoardOption } from '~/types/BoardSearch';
import { containsRestrictedPathChars } from '~/utils/FileUtils';
import { canCreateBoard } from '~/utils/permissions/boardPermissions';

export type CreateBoardFormFields = {
  title: string;
} & FormikValues;

const CreateBoardSchema = Yup.object({
  title: Yup.string()
    .trim()
    .max(255, 'Cannot be longer than 255 characters')
    .test(
      'restricted-char-validation',
      'Characters : and | cannot be used',
      (val) => !containsRestrictedPathChars(val || ''),
    )
    .required('Please enter a board name'),
});

const pageIsValid: FormPageIsValidFunction<CreateBoardFormFields> = (errors, { title }) => !!title && !errors.title;

export type CreatePrivateBoardModalProps = {
  title?: Board['title'];
  trackLocation: AirActionTrackingLocation;
  selectedParentBoard?: InitialBoardOption;
  initialLibrary?: BoardSearchLibrary;
};

export const CreateBoardModal = memo(
  ({
    title,
    trackLocation,
    onClose,
    selectedParentBoard,
    initialLibrary,
  }: AirModalProps<CreatePrivateBoardModalProps>) => {
    const { goToBoardPage } = useGoToBoardPage();
    const { createNewBoard } = useCreateNewBoard();
    const { showToast } = useToasts();
    const currentBoard = useSelector(centralizedBoardSelector);
    const [isLoading, setLoading] = useState(false);

    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });

    const {
      selectedBoard: parentBoard,
      isBoardSelected,
      isLibrarySelected,
      onSelectBoard,
      onSelectLibrary,
      selectedLibrary,
      shouldShowSubBoards,
      shouldShowLibraryBoards,
    } = useSingleBoardSearchState({
      initialBoard: !initialLibrary ? selectedParentBoard || currentBoard : undefined,
      initialLibrary,
    });

    const onBoardSelectChange: PrivateBoardSearchProps['onBoardSelectChange'] = useCallback(
      ({ board, selected }) => {
        onSelectBoard(selected ? board : null);
      },
      [onSelectBoard],
    );

    const { getBoardPermissions } = useBoardPermissionsCache();
    const { getLibraryPermissions } = useLibraryPermissionsCache();

    const getIsBoardDisabled: Required<PrivateBoardSearchProps>['getIsBoardDisabled'] = useCallback(
      (board) => {
        const boardPermissions = getBoardPermissions(board.id);

        if (!canCreateBoard(boardPermissions)) {
          return {
            isDisabled: true,
            message: 'You do not have permission to create boards in this board',
          };
        }
      },
      [getBoardPermissions],
    );

    const getIsLibraryDisabled: Required<PrivateBoardSearchProps>['getIsLibraryDisabled'] = useCallback(
      (library) => {
        const libraryPermissions = getLibraryPermissions(library.id);

        if (!canCreateBoard(libraryPermissions)) {
          return {
            isDisabled: true,
            message: 'You do not have permission to create boards in this library',
          };
        }
      },
      [getLibraryPermissions],
    );

    const onSubmit = useCallback(
      async ({ title }: CreateBoardFormFields) => {
        setLoading(true);

        const trimmedTitle = title.trim();
        const parentId = parentBoard?.id;
        const libraryId = selectedLibrary?.id || parentBoard?.library?.id;

        try {
          const board = await createNewBoard({
            board: {
              title: trimmedTitle,
              description: '',
              parentId: parentId !== 'root' ? parentId : undefined,
              libraryId,
            },
            trackLocation,
            shouldFollow: true,
          });
          if (!board) return;

          showToast(
            <div className="flex w-full items-center justify-center overflow-hidden">
              {'Created board '}
              <ToastLink
                data-testid="CREATED_BOARD_LINK"
                className="flex-1 pl-1.5"
                onClick={() => goToBoardPage({ board, trackLocation: 'created-board-toast' })}
              >
                {trimmedTitle}
              </ToastLink>
            </div>,
          );

          onClose();
        } catch (_error) {
          showToast('An error occurred - please try again later');
        } finally {
          setLoading(false);
        }
      },
      [
        createNewBoard,
        trackLocation,
        showToast,
        goToBoardPage,
        onClose,
        parentBoard?.id,
        parentBoard?.library?.id,
        selectedLibrary?.id,
      ],
    );

    if (showingSubscriptionExpiredModal) {
      return null;
    }

    return (
      <Formik<CreateBoardFormFields>
        validationSchema={CreateBoardSchema}
        onSubmit={onSubmit}
        initialValues={{ title: title || '' }}
      >
        {({ values, errors }) => (
          <Modal isOpen onDismiss={onClose} data-testid="CREATE_BOARD_MODAL">
            <header className="mb-4 flex justify-between gap-4">
              <ModalTitle data-testid="CREATE_BOARD_MODAL_TITLE">Create a board</ModalTitle>
              <ModalCloseButton className="shrink-0" onClick={onClose} />
            </header>
            <ModalDescription className="mb-4" data-testid="CREATE_BOARD_MODAL_DESCRIPTION">
              Boards keep groups of files organized in one place.
            </ModalDescription>
            <Form id="create-board-form">
              <div className="mb-6">
                <FormikField
                  label="Name"
                  name="title"
                  id="title"
                  component={
                    <Input autoFocus autoComplete="off" size="large" data-testid="CREATE_BOARD_MODAL_NAME_INPUT" />
                  }
                />
              </div>

              <div className="mb-6">
                <Label className="mb-1.5">Location</Label>
                <PrivateBoardSearch
                  isBoardSelected={isBoardSelected}
                  onBoardSelectChange={onBoardSelectChange}
                  shouldShowSubBoards={shouldShowSubBoards}
                  shouldShowLibraryBoards={shouldShowLibraryBoards}
                  initialBoardId={parentBoard?.id}
                  className="[&>.workspace-boards]:h-[200px]"
                  onSelectLibrary={onSelectLibrary}
                  isLibrarySelected={isLibrarySelected}
                  getIsBoardDisabled={getIsBoardDisabled}
                  getIsLibraryDisabled={getIsLibraryDisabled}
                />
              </div>
              <footer className="flex justify-end gap-3">
                <Button
                  appearance="ghost"
                  color="grey"
                  onClick={onClose}
                  size="large"
                  data-testid="CREATE_BOARD_MODAL_CANCEL"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="CREATE_BOARD_MODAL_SAVE"
                  isLoading={isLoading}
                  disabled={isLoading || !pageIsValid(errors, values)}
                  appearance="filled"
                  color="blue"
                  size="large"
                  type="submit"
                >
                  Save
                </Button>
              </footer>
            </Form>
          </Modal>
        )}
      </Formik>
    );
  },
);

CreateBoardModal.displayName = 'CreateBoardModal';
